/**
 * @file   src\Pages\UserActivityReport.tsx
 * @brief  User Activity Report page for each carwash.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  Container,
  Table,
  Breadcrumb,
  useNavigate,
  useEffect,
  useOutletContext,
  useState,
  Button,
  useParams,
  Pagination,
  React,
  Badge,
} from "../utils/thirdpartyComponents";
import { userType } from "../utils/enums";
import { pageURLs } from "../utils/pageUrls";
import { AppContextType } from "../interfaces/generalInterface";
import axios from "../config/axiosConfig";
import { CompanyApi, LocationApi, RevenueApi } from "../utils/apiUrls";
import { LocationItem, Option } from "../interfaces/subscriptionInterface";
import SmallLoader from "../components/SmallLoader";
import CWSelect from "../components/CWSelect";
import { MessageToaster } from "../utils/toastUtil";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { MONTHS_OPTIONS } from "../utils/constants";

interface UserActivityRecord {
  UserID?: number;
  FullName?: string;
  Phone?: string;
  LicensePlateNumber?: string | null;
  WashName?: string | null;
  BayName?: string | null;
  LocationName?: string | null;
  activeStatus?: number;
  WashCount?: number | null;
  lastvisit?: string;
}

const UserActivityReport = (props: any) => {
  // Access the context
  const { updateData } = useOutletContext<AppContextType>();
  const carwash_owner =
    props.userType === userType["CARWASH-OWNER"] ? true : false;

  const navigate = useNavigate();
  const timezoneOffset = new Date().getTimezoneOffset();

  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [userRecords, setUserRecords] = useState<UserActivityRecord[]>([]);
  const [locations, setLocations] = useState<Option[]>([]);
  
  const [filters, setFilters] = useState({
    month: "",
    year: "",
    locationId: "0", // "0" represents "All"
  });

  const toaster = new MessageToaster();

  // State to company details
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });

  const pageSize = 10;

  // To display heading
  useEffect(() => {
    getCompany();
    getCompanyAddress();
    getCompanyLocations();
  }, []);

  useEffect(() => {
    handleUpdate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (filters.month && filters.year) {
      getUserActivityReport(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const getCompanyAddress = async () => {
    try {
      const id = params.id ? parseInt(params.id) : 0;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getCompanyLocations = async () => {
    try {
      const carwashGuid = params.id || "";
      const { data } = carwash_owner
        ? await axios.get(LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS)
        : await axios.get(LocationApi.GET_COMPANY_LOCATIONS + carwashGuid);
      if (data.error === 0) {
        const location_list: LocationItem[] = data?.data;
        const transformedArray: Option[] = (location_list || []).map(
          (item) => ({
            label: item.locationName,
            value: item.locationId,
          })
        );
        setLocations([{ label: "All", value: 0 }, ...transformedArray]);
      } else {
        toaster.toastError(data.message);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "User Activity",
      url: carwash_owner
        ? ""
        : `/superadmin/${pageURLs.carwash_locations}/${params.id}`,
      showBackButton:
        props.userType === userType["CARWASH-OWNER"] ? false : true,
      showMainMenu: true,
      activeLink:
        props.userType === userType["CARWASH-OWNER"]
          ? "reports"
          : "manage-car-wash",
    });
  };

  const handleFilterChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return "";
    return moment.utc(dateString).format("MM/DD/YYYY, h:mm A");
  };

  // Function to get member status label
  const getMemberStatusLabel = (status: number | undefined): string => {
    switch (status) {
      case 1:
        return "Active";
      case 2:
        return "Cancelled";
      case 3:
        return "NA";
      case 0:
        return "Inactive";
      default:
        return "Inactive";
    }
  };

  // Function to get member status badge class
  const getMemberStatusBadgeClass = (status: number | undefined): string => {
    switch (status) {
      case 1:
        return "active";
      case 2:
        return "inactive";
      case 3:
        return "info";
      case 0:
        return "inactive";
      default:
        return "inactive";
    }
  };

  // Common function to fetch report data from API
  const fetchReportData = async (pageNo: number, pageSizeParam: number) => {
    if (filters.month === '' || filters.year === '') {
      return null;
    }

    const queryParams = new URLSearchParams();

    // pagination
    const offset = timezoneOffset;
    queryParams.append("offset", offset.toString());
    queryParams.append("pageNo", pageNo.toString());
    queryParams.append("pageSize", pageSizeParam.toString());

    // required carwash
    if (params.id) {
      queryParams.append("carWashId", params.id.toString());
    }

    // filters
    if (filters.month) queryParams.append("month", filters.month);
    if (filters.year) queryParams.append("year", filters.year);
    if (filters.locationId && filters.locationId !== "0") {
      queryParams.append("locationId", filters.locationId);
    }

    const apiUrl = RevenueApi.CARWASH_OWNER_USER_ACTIVITY_REPORT;
    
    const url = `${apiUrl}?${queryParams.toString()}`;
    const { data } = await axios.get(url);

    if (data?.error === 0) {
      // Check if data.data is an array or has a list property
      let records = [];
      let totalCount = 0;
      
      if (Array.isArray(data?.data)) {
        records = data.data;
        totalCount = data?.TotalRows || records.length;
      } else if (data?.data?.list && Array.isArray(data.data.list)) {
        records = data.data.list;
        totalCount = data.data.totalCount || data?.TotalRows || records.length;
      } else if (Array.isArray(data)) {
        records = data;
        totalCount = records.length;
      }
      
      const limit = data?.data?.limit || pageSizeParam || pageSize;
      
      return {
        list: records,
        totalCount: totalCount,
        limit: limit
      };
    }
    
    return null;
  };

  const getUserActivityReport = async (pageNo = 1) => {
    try {
      setLoading(true);
      const result = await fetchReportData(pageNo, pageSize);

      if (result) {
        if (result.list?.length > 0) {
          setUserRecords(result.list);
          setTotalPages(Math.ceil(result.totalCount / result.limit) || 1);
        } else {
          setUserRecords([]);
        }
      } else {
        setUserRecords([]);
      }
    } catch (error: any) {
      console.error("Error fetching user activity report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to fetch report");
      setUserRecords([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadReportAsExcel = async () => {
    if (!filters.month || !filters.year) {
      toaster.toastError("Please select month and year before downloading report.");
      return;
    }

    try {
      setLoading(true);
      const result = await fetchReportData(1, 0); // pageNo = 1, pageSize = 0 to get all records

      if (result && result.list?.length > 0) {
        const worksheetData = result.list.map((item: any) => ({
          "User Name": item?.FullName ?? "",
          "Phone Number": item?.Phone ?? "",
          "License Plate Number": item?.LicensePlateNumber ?? "",
          "Subscription": item?.WashName ?? "",
          "Bay Used": item?.BayName ?? "",
          "Location": item?.LocationName ?? "",
          "Member Status": getMemberStatusLabel(item?.activestatus),
          "Number of Visits": item?.WashCount ?? 0,
          "Last Visit Date": formatDateTime(item?.lastvisit ?? ""),
        }));

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "UserActivity");

        const workbookArray = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });

        const blob = new Blob([workbookArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const fileName = `UserActivity_${filters.year || "AllYears"}_${filters.month || "AllMonths"}.xlsx`;
        saveAs(blob, fileName);
      } else {
        toaster.toastError("No records to download.");
      }
    } catch (error: any) {
      console.error("Error downloading user activity report:", error.message);
      toaster.toastError(error.response?.data?.message || error.message || "Failed to download report");
    } finally {
      setLoading(false);
    }
  };

  // Navigate according to url
  const itemClick = (url: string) => {
    navigate(url);
  };

  const getVisiblePageNumbers = () => {
    const totalPageNumbersToShow = 5;
    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalPageNumbersToShow / 2)
    );
    let endPage = startPage + totalPageNumbersToShow - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
    }

    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const yearOptions = Array.from(
    { length: new Date().getFullYear() - 2024 + 1 },
    (_, i) => {
      const year = 2024 + i;
      return { label: year.toString(), value: year.toString() };
    }
  );

  // Function to fetch company information
  const getCompany = async () => {
    try {
      const param1 = `${params.id}`;
      const { data } = carwash_owner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_DETAILS)
        : await axios.get(CompanyApi.GET_COMPANY + param1);
    } catch (error: any) {
      console.error("Error fetching company information:", error.message);
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="manage-carwash-container order-list-container multi-location-wrap">
      <Container>
        <Breadcrumb>
          {carwash_owner ? (
            <>
              <Breadcrumb.Item
                onClick={() => itemClick(`/${pageURLs.dashboard}`)}
              >
                Dashboard
              </Breadcrumb.Item>
            </>
          ) : (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  itemClick(
                    `/superadmin/${pageURLs.carwash_locations}/${params.id}`
                  );
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
            </>
          )}
          <Breadcrumb.Item active>User Activity</Breadcrumb.Item>
        </Breadcrumb>

        <div className="manage-header-block d-flex justify-content-between align-items-center">
          <div className="search-wrap multi-location-action w-100">
            <div className="result-group d-flex align-items-center">
        
              <CWSelect
                id="selectyear"
                name="selectyear"
                options={yearOptions}
                label="Select Year"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) => {
                  handleFilterChange("year", option[0]?.value?.toString() || "");
                }}
              />
              <CWSelect
                id="selectmonth"
                name="selectmonth"
                options={MONTHS_OPTIONS}
                label="Select Month"
                isMandatory
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                onChange={(option: any) => {
                  handleFilterChange("month", option[0]?.value?.toString() || "");
                }}
              />
              <CWSelect
                id="selectlocation"
                name="selectlocation"
                options={locations}
                label="Select Location"
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select Location"
                onChange={(selected: any) => {
                  const value =
                    Array.isArray(selected) && selected.length > 0
                      ? selected[0].value
                      : "";
                  handleFilterChange("locationId", value.toString());
                }}
              />
            <div className="button-group d-flex">
              <Button
                className="primary-btn"
                variant="primary"
                title="Run Report"
                onClick={() => {
                  // Only fetch when all filters are selected
                  if (filters.month && filters.year) {
                    setCurrentPage(1); // Reset to first page
                    getUserActivityReport(1);
                  } else {
                    toaster.toastError("Please select month and year before running report.");
                  }
                }}
              >
                Run Report
              </Button>
        <Button
          className="secondary-btn ms-3"
          variant="outline-primary"
          title="Download Excel"
          onClick={downloadReportAsExcel}
        >
          Download Excel
        </Button>
            </div>
 
            </div>
          </div>
        </div>
        <div className="cw-table-wrap report-table">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th>User Name</th>
                <th>Phone Number</th>
                <th>License Plate Number</th>
                <th>Subscription</th>
                <th>Bay Used</th>
                <th>Location</th>
                <th>Member Status</th>
                <th>Number of Visits</th>
                <th>Last Visit Date</th>
              </tr>
            </thead>
            <tbody>
              {userRecords.length > 0 ? (
                userRecords.map((item: any, index) => (
                  <tr key={index}>
                    <td>{item?.FullName ?? ""}</td>
                    <td>{item?.Phone ?? ""}</td>
                    <td>{item?.LicensePlateNumber ?? ""}</td>
                    <td>{item?.WashName ?? ""}</td>
                    <td>{item?.BayName ?? ""}</td>
                    <td>{item?.LocationName ?? ""}</td>
                    <td>
                      <Badge
                        className={getMemberStatusBadgeClass(item?.activestatus)}
                      >
                        {getMemberStatusLabel(item?.activestatus)}
                      </Badge>
                    </td>
                    <td>{item?.WashCount ?? 0}</td>
                    <td>{formatDateTime(item?.lastvisit ?? "")}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={9} className="text-center">
                    {loading ? "Loading..." : "No records found"}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          {userRecords && userRecords?.length ? (
            <div className="mt-3 d-flex justify-content-end">
              <Pagination>
                <Pagination.First
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(1)}
                />
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                />
                {getVisiblePageNumbers().map((pageNumber) => (
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === currentPage}
                    onClick={() => handlePageChange(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>
                ))}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                />
                <Pagination.Last
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(totalPages)}
                />
              </Pagination>
            </div>
          ) : null}
        </div>
      </Container>

      {loading && <SmallLoader />}
    </div>
  );
};

export default UserActivityReport;

